import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'restaurant-manager::training-progress';

const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const useTrainingProgress = () => {
  const [progress, setProgress] = useState({});

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const storedValue = window.localStorage.getItem(STORAGE_KEY);
    if (!storedValue) {
      return;
    }
    const parsed = safeParse(storedValue);
    if (parsed && typeof parsed === 'object') {
      setProgress(parsed);
    }
  }, []);

  const persist = useCallback((nextValue) => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextValue));
    } catch {
      // no-op
    }
  }, []);

  const updateProgress = useCallback((trainingId, updates) => {
    if (!trainingId) {
      return;
    }
    setProgress((previous) => {
      const previousEntry = previous?.[trainingId] ?? {};
      const computedUpdates =
        typeof updates === 'function' ? updates(previousEntry) : updates;
      const nextEntry = {
        ...previousEntry,
        ...(computedUpdates ?? {}),
      };
      const nextProgress = {
        ...previous,
        [trainingId]: nextEntry,
      };
      persist(nextProgress);
      return nextProgress;
    });
  }, [persist]);

  const resetTraining = useCallback((trainingId) => {
    if (!trainingId) {
      return;
    }
    setProgress((previous) => {
      if (!previous?.[trainingId]) {
        return previous;
      }
      const nextProgress = { ...previous };
      delete nextProgress[trainingId];
      persist(nextProgress);
      return nextProgress;
    });
  }, [persist]);

  const resetAll = useCallback(() => {
    setProgress({});
    persist({});
  }, [persist]);

  return {
    progress,
    updateProgress,
    resetTraining,
    resetAll,
  };
};

export default useTrainingProgress;
